import React from 'react';
import { DataEntry } from '../types';

const defaultInitialState = {
  selectedDataEntries: new Map(),
  selectedLedgerAccountIds: new Set<string>(),
  deselectedDataEntries: new Map<string, Set<string>>(),
  hasSelectedDataEntryId: () => false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  addSelectedDataEntries: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  removeSelectedDataEntryIds: () => {},
  hasSelectedLedgerAccountId: () => false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  addSelectedLedgerAccountIds: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  removeSelectedLedgerAccountIds: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  addDeselectedDataEntryIds: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  removeDeselectedDataEntryIds: () => {},
  hasDeselectedDataEntryId: () => false,
};

const SelectedDataEntriesContext =
  React.createContext<UseSelectedEntriesContext>(defaultInitialState);

type UseSelectedEntriesState = {
  selectedDataEntries: Map<string, DataEntry>;
  selectedLedgerAccountIds: Set<string>;
  // Map of ledgerAccountId -> dataEntryId[]
  deselectedDataEntries: Map<string, Set<string>>;
};

type UseSelectedEntriesProps = {
  initialState?: Partial<UseSelectedEntriesState>;
};

type UseSelectedEntriesContext = UseSelectedEntriesState & {
  clearSelectedDataEntryIds?: () => void;
  hasSelectedDataEntryId: (ids: string) => boolean;
  addSelectedDataEntries: (dataEntries: DataEntry[]) => void;
  removeSelectedDataEntryIds: (id: string[]) => void;

  clearSelectedLedgerAccountIds?: () => void;
  hasSelectedLedgerAccountId: (ids: string) => boolean;
  addSelectedLedgerAccountIds: (ids: string[]) => void;
  removeSelectedLedgerAccountIds: (id: string[]) => void;

  clearDeselectedDataEntryIds?: () => void;
  addDeselectedDataEntryIds: (ledgerAccountId: string, ids: string[]) => void;
  removeDeselectedDataEntryIds: (ledgerAccountId: string, id: string[]) => void;
  hasDeselectedDataEntryId: (ledgerAccountId: string, id: string) => boolean;
};

export const SelectedDataEntriesProvider = ({
  children,
  initialState = {},
}: React.PropsWithChildren<UseSelectedEntriesProps>): React.ReactElement => {
  const [selectedDataEntries, setSelectedDataEntries] = React.useState<Map<string, DataEntry>>(
    initialState.selectedDataEntries || defaultInitialState.selectedDataEntries,
  );
  const [selectedLedgerAccountIds, setSelectedLedgerAccountIds] = React.useState<Set<string>>(
    initialState.selectedLedgerAccountIds || defaultInitialState.selectedLedgerAccountIds,
  );
  const [deselectedDataEntries, setDeselectedDataEntries] = React.useState<
    Map<string, Set<string>>
  >(initialState.deselectedDataEntries || defaultInitialState.deselectedDataEntries);

  const hasSelectedDataEntryId = (id: string): boolean => {
    return selectedDataEntries.has(id);
  };

  const addSelectedDataEntries = (dataEntries: DataEntry[]): void => {
    const newDataEntries = new Map(selectedDataEntries);
    dataEntries.forEach(dataEntry => {
      newDataEntries.set(dataEntry.id, dataEntry);
    });
    setSelectedDataEntries(newDataEntries);
  };

  const removeSelectedDataEntryIds = (ids: string[]): void => {
    const newDataEntries = new Map(selectedDataEntries);
    ids.forEach(id => {
      newDataEntries.delete(id);
    });
    setSelectedDataEntries(newDataEntries);
  };

  const clearSelectedDataEntryIds = (): void => {
    setSelectedDataEntries(defaultInitialState.selectedDataEntries);
  };

  const hasSelectedLedgerAccountId = (id: string): boolean => {
    return selectedLedgerAccountIds.has(id);
  };

  const addSelectedLedgerAccountIds = (ids: string[]): void => {
    const newLedgerAccountIds = new Set(selectedLedgerAccountIds);
    ids.forEach(id => {
      newLedgerAccountIds.add(id);
    });
    setSelectedLedgerAccountIds(newLedgerAccountIds);
  };

  const removeSelectedLedgerAccountIds = (ids: string[]): void => {
    const newLedgerAccountIds = new Set(selectedLedgerAccountIds);
    ids.forEach(id => {
      newLedgerAccountIds.delete(id);
    });
    setSelectedLedgerAccountIds(newLedgerAccountIds);
  };

  const clearSelectedLedgerAccountIds = (): void => {
    setSelectedLedgerAccountIds(defaultInitialState.selectedLedgerAccountIds);
    // Always clear deselected entries when clearing selected ledger accounts
    clearDeselectedDataEntryIds();
  };

  const addDeselectedDataEntryIds = (ledgerAccountId: string, ids: string[]): void => {
    const newDeselectedDataEntries = new Map(deselectedDataEntries);
    ids.forEach(id => {
      const oldEntries = newDeselectedDataEntries.get(ledgerAccountId);
      newDeselectedDataEntries.set(ledgerAccountId, new Set([id, ...(oldEntries || [])]));
    });
    setDeselectedDataEntries(newDeselectedDataEntries);
  };

  const removeDeselectedDataEntryIds = (ledgerAccountId: string, ids: string[]): void => {
    const newDeselectedDataEntries = new Map(deselectedDataEntries);
    ids.forEach(id => {
      const oldEntries = new Set(newDeselectedDataEntries.get(ledgerAccountId));
      oldEntries.delete(id);
      newDeselectedDataEntries.set(ledgerAccountId, oldEntries);
    });
    if (newDeselectedDataEntries.get(ledgerAccountId)?.size === 0) {
      newDeselectedDataEntries.delete(ledgerAccountId);
    }
    setDeselectedDataEntries(newDeselectedDataEntries);
  };

  const hasDeselectedDataEntryId = (ledgerAccountId: string, id: string): boolean => {
    return deselectedDataEntries.get(ledgerAccountId)?.has(id) || false;
  };

  const clearDeselectedDataEntryIds = (): void => {
    setDeselectedDataEntries(new Map());
  };

  return (
    <SelectedDataEntriesContext.Provider
      value={{
        selectedDataEntries,
        selectedLedgerAccountIds,
        deselectedDataEntries,

        hasSelectedDataEntryId,
        addSelectedDataEntries,
        removeSelectedDataEntryIds,
        clearSelectedDataEntryIds,

        hasSelectedLedgerAccountId,
        addSelectedLedgerAccountIds,
        removeSelectedLedgerAccountIds,
        clearSelectedLedgerAccountIds,

        addDeselectedDataEntryIds,
        removeDeselectedDataEntryIds,
        hasDeselectedDataEntryId,
        clearDeselectedDataEntryIds,
      }}
    >
      {children}
    </SelectedDataEntriesContext.Provider>
  );
};

export const useSelectedEntries = (): UseSelectedEntriesContext => {
  const context = React.useContext(SelectedDataEntriesContext);
  if (context === undefined) {
    throw new Error('useSelectedEntries must be used within a SelectedDataEntriesProvider');
  }
  return context;
};
